import { type TimeFormat, timeParts } from '@/lib/time';

/**
 * Format an ISO timestamp as "Mar 28, 2026, 5:09 PM (about 2 hours ago)".
 */
export function formatTimestamp(
  value: unknown,
  timeFormat: TimeFormat
): string {
  if (!value || typeof value !== 'string') {
    return '';
  }
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }
    const absolute = date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...timeParts(timeFormat),
    });
    const relative = formatRelativeTimeLong(date);
    return relative ? `${absolute} (${relative})` : absolute;
  } catch {
    return '';
  }
}

/** Friendly relative time — "about 2 hours ago", "3 days ago", etc. */
export function formatRelativeTimeLong(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) {
    return '';
  }
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) {
    return 'just now';
  }
  if (diffMins < 60) {
    return diffMins === 1
      ? 'about 1 minute ago'
      : `about ${diffMins} minutes ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return diffHours === 1
      ? 'about 1 hour ago'
      : `about ${diffHours} hours ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return diffDays === 1 ? 'about 1 day ago' : `about ${diffDays} days ago`;
  }
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return diffMonths === 1
      ? 'about 1 month ago'
      : `about ${diffMonths} months ago`;
  }
  const diffYears = Math.floor(diffMonths / 12);
  return diffYears === 1 ? 'about 1 year ago' : `about ${diffYears} years ago`;
}

export function getTimestampLabel(key: string): string {
  if (key === 'ahoy_created_at') {
    return 'Created';
  }
  if (key === 'ahoy_updated_at') {
    return 'Updated';
  }
  return key;
}
